import {
  getBrowserStudioOperations,
  getStudioInteractivityEnabled,
  callApi,
  getOriginalLocationFromStack
} from "./index-hbzdkgeb.mjs";

// src/api/delete-static-file.ts
import { getRemotionEnvironment } from "remotion";
var deleteStaticFile2 = async (relativePath) => {
  if (!getRemotionEnvironment().isStudio) {
    throw new Error("deleteStaticFile() is only available in the Studio");
  }
  if (relativePath.startsWith(window.remotion_staticBase)) {
    relativePath = relativePath.substring(window.remotion_staticBase.length + 1);
  }
  const browserStudioOperations = getBrowserStudioOperations();
  if (browserStudioOperations) {
    return browserStudioOperations.deleteStaticFile({ relativePath });
  }
  if (window.remotion_isReadOnlyStudio) {
    throw new Error("deleteStaticFile() is not available in Read-Only Studio");
  }
  const res = await callApi("/api/delete-static-file", { relativePath });
  return res;
};

// src/api/get-static-files.ts
var warnedServer = false;
var warnedPlayer = false;
var warnServerOnce = () => {
  if (warnedServer) {
    return;
  }
  warnedServer = true;
  console.warn("Called getStaticFiles() on the server. The API is only available in the browser. An empty array was returned.");
};
var warnPlayerOnce = () => {
  if (warnedPlayer) {
    return;
  }
  warnedPlayer = true;
  console.warn("Called getStaticFiles() while using the Remotion Player. The API is only available while using the Remotion Studio. An empty array was returned.");
};
var getStaticFiles2 = () => {
  if (typeof document === "undefined") {
    warnServerOnce();
    return [];
  }
  if (window.remotion_isPlayer) {
    warnPlayerOnce();
    return [];
  }
  return window.remotion_staticFiles;
};

// src/api/pause.ts
import { Internals } from "remotion";
var pause2 = () => {
  Internals.timeValueRef.current?.pause();
};

// src/api/play.ts
import { Internals as Internals2 } from "remotion";
var play2 = (e) => {
  Internals2.timeValueRef.current?.play(e);
};

// src/api/shut-down-studio.ts
import { getRemotionEnvironment as getRemotionEnvironment2 } from "remotion";
var shutDownStudio2 = () => {
  if (!getRemotionEnvironment2().isStudio) {
    throw new Error("shutDownStudio() is only available in the Studio");
  }
  if (getBrowserStudioOperations() !== null) {
    throw new Error("shutDownStudio() is not supported in Browser Studio");
  }
  if (window.remotion_isReadOnlyStudio) {
    throw new Error("shutDownStudio() is not available in read-only Studio");
  }
  return callApi("/api/shutdown-studio", {});
};

// src/components/RenderModal/SchemaEditor/zod-schema-type.ts
var zodSafeParse = (schema, data) => {
  return schema.safeParse(data);
};
var getZodDef = (schema) => {
  if (schema._def)
    return schema._def;
  if (schema._zod)
    return schema._zod.def;
  throw new Error("Invalid zod schema: missing _def and _zod");
};
var v3TypeNameMap = {
  ZodString: "string",
  ZodNumber: "number",
  ZodBoolean: "boolean",
  ZodObject: "object",
  ZodArray: "array",
  ZodEnum: "enum",
  ZodUnion: "union",
  ZodDiscriminatedUnion: "discriminatedUnion",
  ZodOptional: "optional",
  ZodNullable: "nullable",
  ZodDefault: "default",
  ZodTuple: "tuple",
  ZodDate: "date",
  ZodAny: "any",
  ZodUnknown: "unknown",
  ZodBigInt: "bigint",
  ZodNull: "null",
  ZodUndefined: "undefined",
  ZodEffects: "effects",
  ZodLiteral: "literal",
  ZodRecord: "record",
  ZodNever: "never",
  ZodVoid: "void",
  ZodNaN: "nan",
  ZodSymbol: "symbol",
  ZodIntersection: "intersection",
  ZodMap: "map",
  ZodSet: "set",
  ZodLazy: "lazy",
  ZodFunction: "function",
  ZodNativeEnum: "nativeEnum",
  ZodCatch: "catch",
  ZodPromise: "promise",
  ZodBranded: "branded",
  ZodPipeline: "pipeline"
};
var isZodV3Schema = (schema) => {
  const def = getZodDef(schema);
  return "typeName" in def;
};
var getZodSchemaType = (schema) => {
  const def = getZodDef(schema);
  if ("typeName" in def) {
    const { typeName } = def;
    return v3TypeNameMap[typeName] ?? typeName;
  }
  const { type } = def;
  if (type === "union" && def.discriminator !== undefined) {
    return "discriminatedUnion";
  }
  return type;
};
var getZodSchemaDescription = (schema) => {
  if (isZodV3Schema(schema)) {
    return getZodDef(schema).description;
  }
  return schema.description;
};
var REMOTION_BRAND_PREFIX = "__remotion-";
var getUserFacingDescription = (schema) => {
  const description = getZodSchemaDescription(schema);
  if (!description || description.startsWith(REMOTION_BRAND_PREFIX)) {
    return null;
  }
  return description;
};
var getObjectShape = (schema) => {
  const { shape } = getZodDef(schema);
  return typeof shape === "function" ? shape() : shape;
};
var getArrayElement = (schema) => {
  const def = getZodDef(schema);
  return isZodV3Schema(schema) ? def.type : def.element;
};
var getInnerType = (schema) => {
  return getZodDef(schema).innerType;
};
var getEffectsInner = (schema) => {
  return getZodDef(schema).schema;
};
var getLiteralValue = (schema) => {
  const def = getZodDef(schema);
  if (isZodV3Schema(schema)) {
    return def.value;
  }
  return def.values?.[0];
};
var getEnumValues = (schema) => {
  const def = getZodDef(schema);
  if (isZodV3Schema(schema)) {
    return def.values;
  }
  const { entries } = def;
  return Object.values(entries);
};
var getFirstEnumValue = (schema) => {
  const def = getZodDef(schema);
  if (isZodV3Schema(schema)) {
    if (def.typeName === "ZodNativeEnum") {
      const vals = Object.values(def.values);
      return vals[0];
    }
    return def.values[0];
  }
  const { entries } = def;
  const pairs = Object.entries(entries);
  const hasReverseMapping = pairs.some(([key, value]) => key !== String(value));
  if (hasReverseMapping) {
    const forwardPairs = pairs.filter(([key]) => Number.isNaN(Number(key)));
    if (forwardPairs.length > 0) {
      return forwardPairs[0][1];
    }
  }
  return Object.values(entries)[0];
};
var getUnionOptions = (schema) => {
  return getZodDef(schema).options;
};
var getDefaultValue = (schema) => {
  const dv = getZodDef(schema).defaultValue;
  return typeof dv === "function" ? dv() : dv;
};
var getDiscriminator = (schema) => {
  return getZodDef(schema).discriminator;
};
var getDiscriminatedOptionKeys = (schema) => {
  const def = getZodDef(schema);
  const discriminator = getDiscriminator(schema);
  if (isZodV3Schema(schema) && def.optionsMap) {
    return [...def.optionsMap.keys()];
  }
  const options = getUnionOptions(schema);
  return options.map((option) => {
    const shape = getObjectShape(option);
    const discriminatorSchema = shape[discriminator];
    return getLiteralValue(discriminatorSchema);
  });
};
var getDiscriminatedOption = (schema, discriminatorValue) => {
  const def = getZodDef(schema);
  const discriminator = getDiscriminator(schema);
  if (isZodV3Schema(schema) && def.optionsMap) {
    return def.optionsMap.get(discriminatorValue);
  }
  const options = getUnionOptions(schema);
  return options.find((option) => {
    const shape = getObjectShape(option);
    const discriminatorSchema = shape[discriminator];
    return getLiteralValue(discriminatorSchema) === discriminatorValue;
  });
};
var getIntersectionSchemas = (schema) => {
  const def = getZodDef(schema);
  return { left: def.left, right: def.right };
};
var getTupleItems = (schema) => {
  return getZodDef(schema).items;
};
var getRecordValueType = (schema) => {
  return getZodDef(schema).valueType;
};
var getRecordKeyType = (schema) => {
  return getZodDef(schema).keyType;
};
var getPipelineOutput = (schema) => {
  return getZodDef(schema).out;
};
var getPipelineInput = (schema) => {
  return getZodDef(schema).in;
};
var getBrandedInner = (schema) => {
  return isZodV3Schema(schema) ? getZodDef(schema).type : schema;
};

// src/components/RenderModal/SchemaEditor/extract-enum-json-paths.ts
var extractEnumJsonPaths = ({
  schema,
  zodRuntime,
  currentPath,
  zodTypes
}) => {
  const description = getZodSchemaDescription(schema);
  if (zodTypes && description === zodTypes.ZodZypesInternals.REMOTION_MATRIX_BRAND) {
    return [currentPath];
  }
  const typeName = getZodSchemaType(schema);
  switch (typeName) {
    case "object": {
      const shape = getObjectShape(schema);
      const keys = Object.keys(shape);
      return keys.map((key) => {
        return extractEnumJsonPaths({
          schema: shape[key],
          zodRuntime,
          currentPath: [...currentPath, key],
          zodTypes
        });
      }).flat(1);
    }
    case "array": {
      return extractEnumJsonPaths({
        schema: getArrayElement(schema),
        zodRuntime,
        currentPath: [...currentPath, "[]"],
        zodTypes
      });
    }
    case "union": {
      return getUnionOptions(schema).map((option) => {
        return extractEnumJsonPaths({
          schema: option,
          zodRuntime,
          currentPath,
          zodTypes
        });
      }).flat(1);
    }
    case "discriminatedUnion": {
      return getUnionOptions(schema).map((op) => {
        return extractEnumJsonPaths({
          schema: op,
          zodRuntime,
          currentPath,
          zodTypes
        });
      }).flat(1);
    }
    case "literal": {
      return [currentPath];
    }
    case "effects": {
      return extractEnumJsonPaths({
        schema: getEffectsInner(schema),
        zodRuntime,
        currentPath,
        zodTypes
      });
    }
    case "intersection": {
      const { left, right } = getIntersectionSchemas(schema);
      const leftValue = extractEnumJsonPaths({
        schema: left,
        zodRuntime,
        currentPath,
        zodTypes
      });
      const rightValue = extractEnumJsonPaths({
        schema: right,
        zodRuntime,
        currentPath,
        zodTypes
      });
      return [...leftValue, ...rightValue];
    }
    case "tuple": {
      return getTupleItems(schema).map((item, i) => extractEnumJsonPaths({
        schema: item,
        zodRuntime,
        currentPath: [...currentPath, i],
        zodTypes
      })).flat(1);
    }
    case "record": {
      const recordPath = [...currentPath, "{}"];
      const keyResults = extractEnumJsonPaths({
        schema: getRecordKeyType(schema),
        zodRuntime,
        currentPath: recordPath,
        zodTypes
      });
      const valueResults = extractEnumJsonPaths({
        schema: getRecordValueType(schema),
        zodRuntime,
        currentPath: recordPath,
        zodTypes
      });
      return [...keyResults, ...valueResults];
    }
    case "function": {
      throw new Error("Cannot create a value for type function");
    }
    case "enum": {
      return [currentPath];
    }
    case "nativeEnum": {
      return [];
    }
    case "optional":
    case "nullable":
    case "catch": {
      return extractEnumJsonPaths({
        schema: getInnerType(schema),
        zodRuntime,
        currentPath,
        zodTypes
      });
    }
    case "default": {
      return extractEnumJsonPaths({
        schema: getInnerType(schema),
        zodRuntime,
        currentPath,
        zodTypes
      });
    }
    case "promise": {
      return [];
    }
    case "branded": {
      return extractEnumJsonPaths({
        schema: getBrandedInner(schema),
        zodRuntime,
        currentPath,
        zodTypes
      });
    }
    case "pipeline":
    case "pipe": {
      return extractEnumJsonPaths({
        schema: getPipelineOutput(schema),
        zodRuntime,
        currentPath,
        zodTypes
      });
    }
    case "string":
    case "number":
    case "bigint":
    case "boolean":
    case "nan":
    case "date":
    case "symbol":
    case "undefined":
    case "null":
    case "any":
    case "unknown":
    case "never":
    case "void":
    case "map":
    case "lazy":
    case "set":
    case "custom": {
      return [];
    }
    default:
      throw new Error("Not implemented: " + typeName);
  }
};

// src/components/Timeline/use-resolved-stack.ts
import { useContext, useEffect, useState } from "react";
import { Internals as Internals3 } from "remotion";
var resolvedCache = new Map;
var hasResolvedStack = (stack) => {
  if (!stack) {
    return true;
  }
  return resolvedCache.has(stack);
};
var getCachedResolvedStack = (stack) => {
  return stack ? resolvedCache.get(stack) ?? null : null;
};
var inFlight = new Set;
var subscribers = new Map;
var useResolvedStack = (stack) => {
  const updateResolvedStackTrace = useContext(Internals3.SequenceStackTracesUpdateContext);
  const [state, setState] = useState(() => ({
    stack,
    value: stack ? resolvedCache.get(stack) ?? null : null
  }));
  useEffect(() => {
    if (!stack) {
      return;
    }
    if (resolvedCache.has(stack)) {
      const cached = resolvedCache.get(stack);
      setState((prev) => prev.stack === stack && prev.value === cached ? prev : { stack, value: cached });
      return;
    }
    if (!subscribers.has(stack)) {
      subscribers.set(stack, new Set);
    }
    const subs = subscribers.get(stack);
    const onResolved = (value) => {
      setState({ stack, value });
    };
    subs.add(onResolved);
    if (!inFlight.has(stack)) {
      inFlight.add(stack);
      getOriginalLocationFromStack(stack, "sequence").then((frame) => {
        resolvedCache.set(stack, frame);
        updateResolvedStackTrace(stack, frame);
        subs.forEach((fn) => fn(frame));
      }).catch((err) => {
        console.error("Could not get original location of Sequence", err);
        resolvedCache.set(stack, null);
        subs.forEach((fn) => fn(null));
      }).finally(() => {
        inFlight.delete(stack);
      });
    }
    return () => {
      subs.delete(onResolved);
    };
  }, [stack, updateResolvedStackTrace]);
  return state.stack === stack ? state.value : stack ? resolvedCache.get(stack) ?? null : null;
};

// src/helpers/resolved-stack-to-symbolicated.ts
var resolvedStackToSymbolicated = (location) => {
  if (!location?.source) {
    return null;
  }
  return {
    originalFileName: location.source,
    originalLineNumber: location.line,
    originalColumnNumber: location.column,
    originalFunctionName: null,
    originalScriptCode: null
  };
};

// src/api/seek.ts
import { Internals as Internals4 } from "remotion";
var seek2 = (frame) => {
  Internals4.timeValueRef.current?.seek(Math.max(0, frame));
};

// src/visual-controls/visual-control-store.ts
var version = 0;
var listeners = new Set;
var visualControlStore = {
  subscribe(listener) {
    listeners.add(listener);
    return () => {
      listeners.delete(listener);
    };
  },
  getSnapshot() {
    return version;
  },
  emitChange() {
    version++;
    for (const listener of listeners) {
      listener();
    }
  }
};

// src/visual-controls/VisualControls.tsx
import {
  createContext as createContext2,
  createRef,
  useCallback,
  useEffect as useEffect3,
  useImperativeHandle,
  useMemo as useMemo2,
  useRef,
  useState as useState3
} from "react";
import { useRemotionEnvironment } from "remotion";

// src/api/get-zod-schema-from-primitive.ts
function getZodSchemaFromPrimitive(value, z) {
  if (typeof value === "string") {
    return z.string();
  }
  if (typeof value === "number") {
    return z.number();
  }
  let stringified;
  try {
    stringified = JSON.stringify(value);
  } catch {}
  throw new Error(`visualControl(): Specify a schema for this value: ${stringified ?? "[non-serializable value]"}. See https://remotion.dev/docs/studio/visual-control`);
}

// src/components/get-zod-if-possible.tsx
import {
  createContext,
  useContext as useContext2,
  useEffect as useEffect2,
  useMemo,
  useState as useState2
} from "react";
import { jsx } from "react/jsx-runtime";
async function getZodIfPossible() {
  try {
    const { z } = await import("zod");
    return z;
  } catch {
    return null;
  }
}
var getZodV3IfPossible = async () => {
  try {
    const mod = await import("zod/v3");
    return mod;
  } catch {
    return null;
  }
};
var getZTypesIfPossible = async () => {
  try {
    const mod = await import("@remotion/zod-types");
    return mod;
  } catch {
    return null;
  }
};
function useZodIfPossible() {
  const context = useContext2(ZodContext);
  return context?.zod ?? null;
}
var useZodTypesIfPossible = () => {
  const context = useContext2(ZodContext);
  return context?.zodTypes ?? null;
};
var ZodContext = createContext(null);
var ZodProvider = ({ children }) => {
  const [zod, setZod] = useState2(null);
  const [zodV3, setZodV3] = useState2(null);
  const [zodTypes, setZodTypes] = useState2(null);
  useEffect2(() => {
    getZodIfPossible().then((z) => setZod(z));
  }, []);
  useEffect2(() => {
    getZodV3IfPossible().then((z) => setZodV3(z));
  }, []);
  useEffect2(() => {
    getZTypesIfPossible().then((z) => setZodTypes(z));
  }, []);
  const contextValue = useMemo(() => {
    return {
      zod,
      zodV3,
      zodTypes
    };
  }, [zod, zodV3, zodTypes]);
  return /* @__PURE__ */ jsx(ZodContext.Provider, {
    value: contextValue,
    children
  });
};

// src/helpers/interactivity-enabled.ts
var isStudioInteractivityEnabled = () => {
  return getStudioInteractivityEnabled() && (typeof window === "undefined" || !window.remotion_isReadOnlyStudio);
};
var isStudioSelectionEnabled = getStudioInteractivityEnabled;

// src/visual-controls/get-current-edited-value.ts
var getVisualControlEditedValue = ({
  handles,
  key
}) => {
  const handle = handles?.[key];
  if (handle === undefined) {
    return null;
  }
  return handle.unsavedValue;
};

// src/visual-controls/VisualControls.tsx
import { jsx as jsx2 } from "react/jsx-runtime";
var VisualControlsTabActivatedContext = createContext2(false);
var VisualControlsContext = createContext2({
  handles: {}
});
var visualControlRef = createRef();
var SetVisualControlsContext = createContext2({
  updateHandles: () => {
    throw new Error("updateHandles is not implemented");
  },
  updateValue: () => {
    throw new Error("updateValue is not implemented");
  },
  visualControl: () => {
    throw new Error("visualControl is not implemented");
  }
});
var VisualControlsProvider = ({ children }) => {
  const imperativeHandles = useRef({});
  const [handles, setHandles] = useState3({});
  const state = useMemo2(() => {
    return {
      handles
    };
  }, [handles]);
  const setControl = useCallback((key, value) => {
    const existingHandle = imperativeHandles.current?.[key];
    const currentSavedState = existingHandle?.valueInCode;
    const changedSavedValue = value.valueInCode !== currentSavedState;
    const changedUnsavedValue = existingHandle === undefined && value.valueInCode !== undefined;
    imperativeHandles.current = {
      ...imperativeHandles.current,
      [key]: {
        ...value,
        unsavedValue: existingHandle !== undefined && !changedSavedValue ? existingHandle.unsavedValue : value.valueInCode,
        valueInCode: value.valueInCode
      }
    };
    return {
      changed: changedSavedValue || changedUnsavedValue,
      currentValue: getVisualControlEditedValue({
        key,
        handles: imperativeHandles.current
      })
    };
  }, []);
  const z = useZodIfPossible();
  const changedRef = useRef(false);
  const env = useRemotionEnvironment();
  const visualControl = useCallback(function(key, value, schema) {
    if (handles && false) {}
    if (!env.isStudio) {
      return value;
    }
    if (!isStudioInteractivityEnabled()) {
      return value;
    }
    if (!z) {
      return value;
    }
    const { changed, currentValue } = setControl(key, {
      valueInCode: value,
      schema: schema ?? getZodSchemaFromPrimitive(value, z),
      stack: new Error().stack
    });
    if (changed) {
      changedRef.current = true;
    }
    return currentValue;
  }, [setControl, handles, z, env.isStudio]);
  const updateHandles = useCallback(() => {
    setHandles(() => {
      return imperativeHandles.current;
    });
  }, []);
  const updateValue = useCallback((key, value) => {
    imperativeHandles.current = {
      ...imperativeHandles.current,
      [key]: {
        ...imperativeHandles.current[key],
        unsavedValue: value
      }
    };
    updateHandles();
    visualControlStore.emitChange();
  }, [updateHandles]);
  useImperativeHandle(visualControlRef, () => {
    return {
      globalVisualControl: visualControl
    };
  }, [visualControl]);
  useEffect3(() => {
    visualControlStore.emitChange();
  }, [visualControl]);
  useEffect3(() => {
    const callback = () => {
      if (imperativeHandles.current) {
        updateHandles();
        changedRef.current = false;
      }
    };
    const interval = setInterval(callback, 100);
    return () => {
      clearInterval(interval);
    };
  }, [updateHandles]);
  const setState = useMemo2(() => {
    return {
      setControl,
      updateHandles,
      updateValue,
      visualControl
    };
  }, [setControl, updateHandles, updateValue, visualControl]);
  return /* @__PURE__ */ jsx2(VisualControlsTabActivatedContext.Provider, {
    value: Object.keys(state.handles).length > 0,
    children: /* @__PURE__ */ jsx2(VisualControlsContext.Provider, {
      value: state,
      children: /* @__PURE__ */ jsx2(SetVisualControlsContext.Provider, {
        value: setState,
        children
      })
    })
  });
};

// src/api/watch-public-folder.ts
import { getRemotionEnvironment as getRemotionEnvironment3 } from "remotion";
var WATCH_REMOTION_STATIC_FILES = "remotion_staticFilesChanged";
var watchPublicFolder2 = (callback) => {
  if (!getRemotionEnvironment3().isStudio) {
    console.warn("The watchPublicFolder() API is only available while using the Remotion Studio.");
    return { cancel: () => {
      return;
    } };
  }
  if (window.remotion_isReadOnlyStudio && !getBrowserStudioOperations()) {
    throw new Error("watchPublicFolder() is not available in read-only Studio");
  }
  const emitUpdate = () => {
    callback(getStaticFiles2());
  };
  window.addEventListener(WATCH_REMOTION_STATIC_FILES, emitUpdate);
  const cancel = () => {
    return window.removeEventListener(WATCH_REMOTION_STATIC_FILES, emitUpdate);
  };
  return { cancel };
};

// src/api/write-static-file.ts
var writeStaticFile2 = async ({
  contents,
  filePath
}) => {
  if (filePath.includes("\\")) {
    return Promise.reject(new Error("File path cannot contain backslashes"));
  }
  const browserStudioOperations = getBrowserStudioOperations();
  if (browserStudioOperations) {
    return browserStudioOperations.writeStaticFile({ contents, filePath });
  }
  if (window.remotion_isReadOnlyStudio) {
    throw new Error("writeStaticFile() is not available in read-only Studio");
  }
  const url = new URL(`${window.remotion_staticBase}/api/add-asset`, window.location.origin);
  url.search = new URLSearchParams({
    filePath
  }).toString();
  const response = await fetch(url, {
    method: "POST",
    body: contents
  });
  if (!response.ok) {
    const jsonResponse = await response.json();
    throw new Error(jsonResponse.error);
  }
};

export { deleteStaticFile2, getStaticFiles2, pause2, play2, shutDownStudio2, zodSafeParse, getZodDef, isZodV3Schema, getZodSchemaType, getZodSchemaDescription, getUserFacingDescription, getObjectShape, getArrayElement, getInnerType, getEffectsInner, getLiteralValue, getEnumValues, getFirstEnumValue, getUnionOptions, getDefaultValue, getDiscriminator, getDiscriminatedOptionKeys, getDiscriminatedOption, getIntersectionSchemas, getTupleItems, getRecordValueType, getRecordKeyType, getPipelineOutput, getPipelineInput, getBrandedInner, extractEnumJsonPaths, hasResolvedStack, getCachedResolvedStack, useResolvedStack, resolvedStackToSymbolicated, seek2, visualControlStore, useZodIfPossible, useZodTypesIfPossible, ZodProvider, isStudioInteractivityEnabled, isStudioSelectionEnabled, getVisualControlEditedValue, VisualControlsContext, visualControlRef, SetVisualControlsContext, VisualControlsProvider, watchPublicFolder2, writeStaticFile2 };
